/**
 * ReviewModal — "Rate My Pluck" submission Dialog.
 *
 * Customer-only. Surfaces from the past-booking card on
 * /dashboard/customer/bookings. Submits to POST /api/reviews
 * (gated server-side to status="completed" + owned-by-caller).
 *
 * Props:
 *   open           : bool
 *   onOpenChange   : (next: bool) -> void
 *   booking        : the booking being reviewed (used for header copy)
 *   onSubmitted    : (review: Review) -> void  -- parent updates its
 *                    in-memory review map after a successful POST.
 *
 * UX rules:
 *   - Submit disabled until rating ≥ 1.
 *   - Comment optional, ≤ 500 chars (server matches).
 *   - One-shot only — backend's unique index on booking_id is the
 *     enforcement; UI relies on this & does NOT re-render the CTA
 *     after a success (parent flips to "Reviewed" pill).
 *   - Slate `#2B3942` on orange `#F89623` for the primary submit CTA,
 *     matching the locked in-app CTA brand rule.
 */
import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import StarRating from "@/components/reviews/StarRating";
import ImpersonationConfirmGate, {
  useImpersonationGate,
} from "@/components/ImpersonationConfirmGate";

const MAX_COMMENT = 500;

export default function ReviewModal({
  open,
  onOpenChange,
  booking,
  onSubmitted,
}) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Reset transient state every time the modal opens for a different booking.
  React.useEffect(() => {
    if (open) {
      setRating(0);
      setComment("");
      setSubmitting(false);
    }
  }, [open, booking?.id]);

  const { requestAction, gateProps } = useImpersonationGate();

  const reallySubmit = async () => {
    if (rating < 1) return;
    setSubmitting(true);
    try {
      const trimmed = comment.trim();
      const { data } = await api.post("/reviews", {
        booking_id: booking.id,
        rating,
        comment: trimmed.length > 0 ? trimmed : null,
      });
      toast.success("Thanks for your Pluck rating!");
      onSubmitted?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) || e.message
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = () => {
    if (rating < 1) return;
    requestAction("submit Pluck rating", reallySubmit);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !submitting && onOpenChange(next)}>
      <DialogContent
        className="max-w-md rounded-3xl"
        data-testid="review-modal"
      >
        <DialogHeader>
          <DialogTitle
            className="font-heading text-2xl text-pp-text"
            data-testid="review-modal-title"
          >
            Rate My Pluck
          </DialogTitle>
          {booking?.provider_business_name && (
            <DialogDescription data-testid="review-modal-subtitle">
              How did {booking.provider_business_name} do?
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex flex-col items-center gap-2">
            <StarRating
              value={rating}
              onChange={setRating}
              size="lg"
              testidPrefix="review-modal-star"
              ariaLabel="Pluck rating"
            />
            <span
              className="text-xs font-semibold uppercase tracking-wider text-muted-foreground"
              data-testid="review-modal-rating-label"
            >
              {rating === 0 ? "Tap a star" : `${rating} of 5`}
            </span>
          </div>

          <div>
            <label
              htmlFor="review-comment"
              className="mb-1 block text-sm font-semibold text-pp-text"
            >
              Comment <span className="font-normal text-muted-foreground">(optional)</span>
            </label>
            <Textarea
              id="review-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value.slice(0, MAX_COMMENT))}
              placeholder="Tell us how it went…"
              className="min-h-[96px] rounded-2xl"
              data-testid="review-modal-comment"
            />
            <p
              className="mt-1 text-right text-xs text-muted-foreground"
              data-testid="review-modal-comment-counter"
            >
              {comment.length}/{MAX_COMMENT}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            disabled={submitting}
            onClick={() => onOpenChange(false)}
            className="rounded-full"
            data-testid="review-modal-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={rating < 1 || submitting}
            onClick={handleSubmit}
            className="rounded-full bg-pp-orange text-pp-cta-text hover:bg-pp-orange-hover disabled:opacity-60"
            data-testid="review-modal-submit"
          >
            {submitting ? "Submitting…" : "Submit"}
          </Button>
        </DialogFooter>
      </DialogContent>
      <ImpersonationConfirmGate {...gateProps} />
    </Dialog>
  );
}
